import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// Reusable components
import StatusBadge from "../common/StatusBadge";
import Loader from "../common/Loader";
import TimeLineItem2 from "../common/TimeLineItem2";
import FilePreviewModal from "../common/FilePreviewModal";
import DocumentPreviewViewer from "../common/DocumentPreviewViewer";

// Config and styles
import {
  ROLE_PERMISSIONS,
  ID_CARD_STATUSES,
  DEFAULT_PERMISSIONS,
  STATUS_ACTIONS,
} from "../common/IdCardDetailsConfig";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../styles/GrievanceDetails.css";

// Redux actions
import {
  fetchCardDetails,
  fetchCardHistory,
  updateCardStatus,
} from "../../redux/actions/grievanceActions1";

const IdCardDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // Extract data from location state
  const { id, esm, userName, centerName } = location.state || {};

  // Get data from Redux store
  const { cardDetails, cardHistory } = useSelector((state) => state.grievances);
  const { user } = useSelector((state) => state.grievances);
  const userRole = user?.role;

  // Get permissions based on user role
  const userPermissions = ROLE_PERMISSIONS[userRole] || DEFAULT_PERMISSIONS;

  // Component state
  const [idCard, setIdCard] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [fileType, setFileType] = useState("");
  const [remarks, setRemarks] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load data
  useEffect(() => {
    dispatch(fetchCardDetails(id));
    dispatch(fetchCardHistory(id));
  }, [id, dispatch]);

  // Update local state when cardDetails changes
  useEffect(() => {
    if (cardDetails) {
      setIdCard(cardDetails);
      setIsLoading(false);

      // If card is approved or rejected and current tab is updateStatus, switch to overview tab
      if (
        (cardDetails.STATUS_ID === "26" ||
          cardDetails.STATUS_ID === "27" ||
          cardDetails.STATUS_ID === "24") &&
        activeTab === "updateStatus"
      ) {
        setActiveTab("overview");
      }
    }
  }, [cardDetails, activeTab]);

  // Set initial active tab based on permissions
  useEffect(() => {
    if (userPermissions.availableTabs.length > 0) {
      setActiveTab(userPermissions.availableTabs[0]);
    }
  }, [userPermissions]);

  // If still loading, show loader
  if (isLoading) {
    return <Loader />;
  }

  const handleTimelineAttachmentPreview = (state, attachment, name) => {
    setIsLoading(state);
    setPreviewAttachment(attachment);
    setFileType(name);
    if (!state) {
      setIsPreviewModalOpen(true);
    }
  };

  const closePreviewModal = () => {
    setIsPreviewModalOpen(false);
    setPreviewAttachment(null);
  };

  // Check if the Update Status tab should be visible
  const shouldShowUpdateStatusTab = () => {
    // Hide the Update Status tab if card is already approved or rejected
    if (
      cardDetails?.STATUS_ID === "26" ||
      cardDetails?.STATUS_ID === "27" ||
      cardDetails?.STATUS_ID === "24"
    ) {
      return false;
    }
    return (
      userPermissions.availableTabs.includes("updateStatus") &&
      userPermissions.canUpdate
    );
  };

  const handleStatusUpdate = async (statusId) => {
    // Check if status action requires remarks
    const statusAction = STATUS_ACTIONS.find(
      (action) => action.statusId === statusId
    );
    const requiresRemarks = statusAction?.requiresRemarks || false;

    if (requiresRemarks && !remarks.trim()) {
      alert("Please specify which documents are needed in the remarks field");
      return;
    }

    if (!requiresRemarks && !remarks.trim()) {
      alert("Please enter remarks before updating status");
      return;
    }

    setIsSubmitting(true);

    // Create payload for status update
    const payload = {
      cardId: id,
      statusId: statusId,
      remarks: remarks,
    };

    // Dispatch action to update status
    const res = await dispatch(updateCardStatus(payload));
  
    // Refresh data after successful update
    await dispatch(fetchCardDetails(id));
    await dispatch(fetchCardHistory(id));

    setRemarks("");
    setIsSubmitting(false);
    if (res.rCode == 0) {
      alert(res.rMessage || "Status updated successfully");
    } else {
      alert(res.rMessage || "error");
    }
  };

  // Helper function to render applicant name based on ID card type
  const renderApplicantName = () => {
    if (!cardDetails) return "N/A";

    switch (cardDetails.IDCARD_TYPE_ID) {
      case "1":
        return "Ex-servicemen";
      case "2":
        return idCard?.WIDOW_NAME || "N/A";
      case "3":
        return idCard?.DEPENDANT_NAME || "N/A";
      default:
        return "N/A";
    }
  };

  // Render type-specific details based on ID card type
  const renderTypeSpecificDetails = () => {
    if (!cardDetails) return null;

    switch (cardDetails.IDCARD_TYPE_ID) {
      case "1":
        return (
          <div className="col-md-12">
            <h4 className="mb-3">Service Information</h4>
            <div className="table-responsive">
              <table className="table table-hover">
                <tbody>
                  <tr>
                    <th>Type of Retirement</th>
                    <td>{idCard?.TYPE_OF_RETIREMENT || "N/A"}</td>
                  </tr>
                  <tr>
                    <th>Qualifying Service</th>
                    <td>{idCard?.QUALIFYING_SERVICE || "N/A"} years</td>
                  </tr>
                  <tr>
                    <th>Last Pay Drawn</th>
                    <td>₹{idCard?.LAST_PAY_DRAWN || "N/A"}</td>
                  </tr>
                  <tr>
                    <th>Decoration</th>
                    <td>{idCard?.DECORATION || "N/A"}</td>
                  </tr>
                  <tr>
                    <th>Identification Marks</th>
                    <td>{idCard?.IDENTIFICATION_MARKS || "N/A"}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      case "2":
        return (
          <div className="col-md-12">
            <h4 className="mb-3">Widow Information</h4>
            <div className="table-responsive">
              <table className="table table-hover">
                <tbody>
                  <tr>
                    <th>Name</th>
                    <td>{idCard?.WIDOW_NAME || "N/A"}</td>
                  </tr>
                  <tr>
                    <th>Date of Birth</th>
                    <td>{idCard?.WIDOW_DOB || "N/A"}</td>
                  </tr>
                  <tr>
                    <th>Height</th>
                    <td>
                      {idCard?.WIDOW_HEIGHT
                        ? `${idCard.WIDOW_HEIGHT} cm`
                        : "N/A"}
                    </td>
                  </tr>
                  <tr>
                    <th>Hair Color</th>
                    <td>{idCard?.WIDOW_HAIR_COLOR || "N/A"}</td>
                  </tr>
                  <tr>
                    <th>Eye Color</th>
                    <td>{idCard?.WIDOW_EYE_COLOR || "N/A"}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      case "3":
        return (
          <div className="col-md-12">
            <h4 className="mb-3">Dependent Information</h4>
            <div className="table-responsive">
              <table className="table table-hover">
                <tbody>
                  <tr>
                    <th>Name</th>
                    <td>{idCard?.DEPENDANT_NAME || "N/A"}</td>
                  </tr>
                  <tr>
                    <th>Date of Birth</th>
                    <td>{idCard?.DEPENDANT_DOB || "N/A"}</td>
                  </tr>
                  <tr>
                    <th>Height</th>
                    <td>
                      {idCard?.DEPENDANT_HEIGHT
                        ? `${idCard.DEPENDANT_HEIGHT} cm`
                        : "N/A"}
                    </td>
                  </tr>
                  <tr>
                    <th>Hair Color</th>
                    <td>{idCard?.DEPENDANT_HAIR_COLOR || "N/A"}</td>
                  </tr>
                  <tr>
                    <th>Eye Color</th>
                    <td>{idCard?.DEPENDANT_EYE_COLOR || "N/A"}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      default:
        return <div>No specific details available for this ID card type.</div>;
    }
  };

  // Styles for action buttons
  const actionButtonBaseStyle = {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px 12px",
    color: "white",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    height: "50px",
    // width: "auto",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    fontWeight: "500",
    fontSize: "13px",
  };

  return (
    <div className="grievance-details-container">
      <DocumentPreviewViewer
        isOpen={isPreviewModalOpen}
        onClose={closePreviewModal}
        attachment={previewAttachment}
        fileTypes={fileType}
      />

      {/* Back Button */}
      <div className="floating-actions">
        <button
          className="fab-button"
          onClick={() => navigate("/IdCardList")}
          title="Back to ID cards"
        >
          <i className="bi bi-arrow-left"></i>
        </button>
      </div>

      {/* ID Card Header */}
      <div className="grievance-details-header">
        <div className="grievance-details-title">
          <div className="title-icon">
            <i className="bi bi-person-vcard-fill"></i>
          </div>
          <div>
            <h2>ID{idCard?.CARD_ID?.toString().padStart(10, "0")}</h2>
            <p className="grievance-subtitle">
              {idCard?.IDCARD_TYPE_NAME} • {idCard?.ENTRY_DATETIME}
            </p>
          </div>
        </div>
        <StatusBadge status={idCard?.STATUS_NAME} isGrant={true} size="large" />
      </div>

      {/* Tab Navigation - Only show tabs based on user role permissions */}
      <div className="grievance-details-tabs">
        {userPermissions.availableTabs.includes("overview") && (
          <button
            className={`tab-button ${activeTab === "overview" ? "active" : ""}`}
            onClick={() => setActiveTab("overview")}
          >
            <i className="bi bi-info-circle"></i> Overview
          </button>
        )}

        {userPermissions.availableTabs.includes("timeline") && (
          <button
            className={`tab-button ${activeTab === "timeline" ? "active" : ""}`}
            onClick={() => setActiveTab("timeline")}
          >
            <i className="bi bi-clock-history"></i> Status Trail
            {cardHistory?.updates?.length > 0 && (
              <span className="tab-badge">{cardHistory?.updates?.length}</span>
            )}
          </button>
        )}

        {userPermissions.availableTabs.includes("details") && (
          <button
            className={`tab-button ${activeTab === "details" ? "active" : ""}`}
            onClick={() => setActiveTab("details")}
          >
            <i className="bi bi-list-ul"></i> ID Card Details
          </button>
        )}

        {userPermissions.availableTabs.includes("updateStatus") &&
          shouldShowUpdateStatusTab() && (
            <button
              className={`tab-button ${
                activeTab === "updateStatus" ? "active" : ""
              }`}
              onClick={() => setActiveTab("updateStatus")}
            >
              <i className="bi bi-pencil-square"></i> Update Status
            </button>
          )}
      </div>

      {/* Tab Content */}
      <div className="grievance-details-content">
        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="grievance-details-panel">
            {/* Applicant Information Card */}
            <div className="grievance-details-card applicant-card">
              <div className="card-header">
                <h3>
                  <i className="bi bi-person"></i> Applicant Information
                </h3>
              </div>
              <div className="card-body">
                <div className="applicant-name">{userName||"N/A"}</div>

                <div className="info-grid">
                  <div className="info-item">
                    <div className="info-icon">
                      <i className="bi bi-calendar2"></i>
                    </div>
                    <div>
                      <div className="info-label">Submission Date</div>
                      <div className="info-value">
                        {idCard?.ENTRY_DATETIME || "N/A"}
                      </div>
                    </div>
                  </div>

                  <div className="info-item">
                    <div className="info-icon">
                      <i className="bi bi-building"></i>
                    </div>
                    <div>
                      <div className="info-label">ARESA Center</div>
                      <div className="info-value">{centerName || "N/A"}</div>
                    </div>
                  </div>

                  <div className="info-item">
                    <div className="info-icon">
                      <i className="bi bi-credit-card-2-front"></i>
                    </div>
                    <div>
                      <div className="info-label">ID Card Type</div>
                      <div className="info-value">
                        {idCard?.IDCARD_TYPE_NAME || "N/A"}
                      </div>
                    </div>
                  </div>

                  <div className="info-item">
                    <div className="info-icon">
                      <i className="bi bi-droplet"></i>
                    </div>
                    <div>
                      <div className="info-label">Blood Group</div>
                      <div className="info-value">
                        {idCard?.BLOOD_GROUP || "N/A"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ID Card Details Card */}
            <div className="grievance-details-card description-card">
              <div className="card-header">
                <h3>
                  <i className="bi bi-card-text"></i> ID Card Information
                </h3>
              </div>
              <div className="card-body">
                <div className="category-badge">
                  <i className="bi bi-tag-fill"></i>
                  <span>{idCard?.IDCARD_TYPE_NAME}</span>
                </div>

                <div className="description-box">
                  <div className="description-title">
                    <i className="bi bi-card-text"></i> Remarks
                  </div>
                  <div className="description-content">
                    {idCard?.REMARKS || "No remarks provided"}
                  </div>
                </div>

                <div className="description-box">
                  <div className="description-title">
                    <i className="bi bi-award"></i> Decoration
                  </div>
                  <div className="description-content">
                    {idCard?.DECORATION || "None"}
                  </div>
                </div>

                <div className="status-action-section">
                  <div className="current-status">
                    <p className="status-label">Current Status</p>
                    <StatusBadge status={idCard?.STATUS_NAME} isGrant={true} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Timeline Tab */}
        {activeTab === "timeline" && (
          <div className="grievance-details-panel full-width">
            <div className="grievance-details-card">
              <div className="card-header">
                <h3>
                  <i className="bi bi-clock-history"></i> ID Card Timeline
                </h3>
              </div>
              <div className="card-body">
                {cardHistory?.updates?.length > 0 ? (
                  <div className="timeline-container">
                    {cardHistory?.updates.map((entry, index) => (
                      <TimeLineItem2
                        key={index}
                        status_name={entry.status_name}
                        action={entry.remarks}
                        date={entry.entry_datetime}
                        comments={entry.remarks}
                        attachments={entry.attachments || []}
                        user_name={entry.user_name}
                        onPreviewAttachment={handleTimelineAttachmentPreview}
                        isLast={index === cardHistory.updates.length - 1}
                        isFirst={index === 0}
                        isIdCard={true}
                        isLoading={isLoading}
                        type="idcard"
                      />
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <div className="empty-icon">
                      <i className="bi bi-clock-history"></i>
                    </div>
                    <p>No timeline events available for this ID card yet.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ID Card Details Tab */}
        {activeTab === "details" && (
          <div className="grievance-details-panel full-width">
            <div className="grievance-details-card">
              <div className="card-header">
                <h3>
                  <i className="bi bi-person-vcard"></i> Detailed Information
                </h3>
              </div>
              <div className="card-body">{renderTypeSpecificDetails()}</div>
            </div>
          </div>
        )}

        {/* Update Status Tab - Only visible to users with update permissions */}
        {activeTab === "updateStatus" && shouldShowUpdateStatusTab() && (
          <div className="grievance-details-panel full-width">
            <div className="grievance-details-card">
              <div className="card-header">
                <h3>
                  <i className="bi bi-pencil-square"></i> Update ID Card Status
                </h3>
              </div>
              <div className="card-body">
                <div className="row mb-4">
                  <div className="col-12">
                    <div
                      className="status-card"
                      style={{
                        border: "1px solid #ddd",
                        borderRadius: "8px",
                        padding: "15px",
                        marginBottom: "20px",
                      }}
                    >
                      <h5>
                        <i className="bi bi-info-circle"></i> Current Status
                      </h5>
                      <div
                        style={{
                          background: "#f8f9fa",
                          padding: "10px 15px",
                          borderRadius: "6px",
                          border: "1px solid #dee2e6",
                          fontWeight: "500",
                        }}
                      >
                        {idCard?.STATUS_NAME || "Unknown"}
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="remarks" className="form-label fw-bold">
                        <i className="bi bi-chat-text"></i> Remarks
                      </label>
                      <textarea
                        id="remarks"
                        className="form-control"
                        rows="4"
                        placeholder="Enter your remarks or comments about this status change..."
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        style={{ border: "1px solid #ced4da" }}
                      ></textarea>
                      <div className="form-text text-muted">
                        Please provide detailed information about why you're
                        updating the status.
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status Action Buttons - Only show actions allowed for the user role */}
                <div
                  className="status-action-container"
                  style={{ marginTop: "30px" }}
                >
                  <h5 className="mb-3">Choose Action</h5>

                  {/* Display message if card is already approved or rejected */}
                  {cardDetails?.STATUS_ID === "26" ||
                  cardDetails?.STATUS_ID === "27" ? (
                    <div className="alert alert-info">
                      <i className="bi bi-info-circle"></i> This ID Card has
                      been{" "}
                      {cardDetails?.STATUS_ID === "26"
                        ? "approved"
                        : "rejected"}{" "}
                      and cannot be modified further.
                    </div>
                  ) : (
                    <div
                      className="action-buttons"
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(150px, 1fr))",
                        gap: "10px",
                      }}
                    >
                      {/* Only render status buttons that are allowed for this user's role and visible for current status */}
                      {STATUS_ACTIONS.map((action) => {
                        // Only show status actions allowed for this user role and current card status

                        return (
                          <button
                            key={action.statusId}
                            className="action-button"
                            onClick={() => handleStatusUpdate(action.statusId)}
                            disabled={isSubmitting}
                            style={{
                              ...actionButtonBaseStyle,
                              ...action.style,
                            }}
                          >
                            <i
                              className={`bi ${action.icon}`}
                              style={{ fontSize: "16px", marginRight: "8px" }}
                            ></i>
                            <span>{action.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div
                  className="cancel-action"
                  style={{ marginTop: "20px", textAlign: "center" }}
                >
                  {!(
                    idCard?.STATUS_ID === "26" || idCard?.STATUS_ID === "27"
                  ) && (
                    <button
                      className="btn"
                      onClick={() => {
                        setRemarks("");
                      }}
                      disabled={isSubmitting}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "6px 15px",
                        border: "1px solid #6c757d",
                        background: "white",
                        color: "#6c757d",
                        borderRadius: "4px",
                        cursor: "pointer",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                        fontWeight: "500",
                        fontSize: "13px",
                        transition: "all 0.2s ease",
                      }}
                    >
                      <i
                        className="bi bi-arrow-counterclockwise"
                        style={{ marginRight: "6px" }}
                      ></i>{" "}
                      Clear Form
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {isLoading && <Loader></Loader>}
    </div>
  );
};

export default IdCardDetails;
